import type { AclEntityMinInfo } from "@eu.mdotm.api/sphere";
import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import type { ShareACLEntry, ShareDialogProps } from "../ShareDialog";
import { roleFromAcl, type Roles } from "../checkers/all";
import type { AclProviderResult } from "./types";

type UserPermissionEntry = { entry: RichAccessControl & { userId: string }; role?: Roles };

export function extractValidUserPermissions(
	entityAclList: Array<AclProviderResult>,
	area: ShareDialogProps["area"],
): UserPermissionEntry[] {
	return entityAclList.flatMap(
		(entity) =>
			entity.customerEntityAcl?.flatMap((entry): [UserPermissionEntry] | [] =>
				entry.userId
					? [
							{
								entry: { ...entry, userId: entry.userId },
								role: entry.permissions?.length === 0 ? undefined : roleFromAcl(area, entry.userId, [entry]),
							},
						]
					: [],
			) ?? [],
	);
}

export function mergeUserRoleAcrossEntities(userEntries: UserPermissionEntry[]): ShareACLEntry | null {
	const first = userEntries.at(0);
	if (!first) {
		return null;
	}

	const firstWithRole = userEntries.find((e) => e.role !== undefined);
	if (!firstWithRole) {
		return {
			...first.entry,
			permissions: first.entry.permissions ?? [],
			permissionState: undefined,
		};
	}

	const hasConsistentRole = userEntries.every(({ role }) => role === firstWithRole.role);
	return {
		...first.entry,
		permissions: hasConsistentRole ? (firstWithRole.entry.permissions ?? []) : [],
		permissionState: hasConsistentRole ? undefined : "mixed",
	};
}

export function deduplicateLinkedEntities(entityAclList: Array<AclProviderResult>): AclEntityMinInfo[] {
	const allLinkedEntities = entityAclList.flatMap((entity) => entity.linkedAclEntities ?? []);
	return [...Map.groupBy(allLinkedEntities, (entity) => entity.identifier ?? entity.name ?? "").values()].map(
		(group) => group[0],
	);
}

export function mergeMultiEntityPermissions(
	entityAclList: Array<AclProviderResult>,
	area: ShareDialogProps["area"],
): { acl: ShareACLEntry[]; linkedEntities: AclEntityMinInfo[] } {
	const allUserPermissions = extractValidUserPermissions(entityAclList, area);
	const permissionsByUser = Map.groupBy(allUserPermissions, ({ entry }) => entry.userId);

	const acl = [...permissionsByUser.values()]
		.map((userEntries) => mergeUserRoleAcrossEntities(userEntries))
		.filter((entry): entry is ShareACLEntry => entry !== null);

	const linkedEntities = deduplicateLinkedEntities(entityAclList);

	return { acl, linkedEntities };
}
